import PropTypes from 'prop-types';
import { Box, Button, Grid, TextField, Typography, Stack } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';
import ModalBox from './ModalBox';
import { useTheme } from '@emotion/react';
import { useRef } from 'react';
import { useEffect } from 'react';

/**
 * The `Popup` is a wrapper for the most of alerts and popups.
 * 
 * These are the default props for popup modal
 *
 * ```js
 * {
 *     primaryBtnTxt: 'Ok',
 *     primaryBtnStyles: {},
 *     onPrimaryClick: () => { },
 *     secondaryBtnTxt: 'Cancel',
 *     secondaryBtnStyles: {},
 *     onSecondaryClick: () => { },
 *     show: false,
 *     title: '',
 *     variant: 'info',
 *     consent: false,
 * }
 * ```
 */
const Popup = (props) => {
    const theme = useTheme()
    const [isPrimaryLoading, setIsPrimaryLoading] = useState(false)
    const [isSecondaryLoading, setIsSecondaryLoading] = useState(false)
    const [disablePrimary, setDisablePrimary] = useState(props.consent)
    const isPrimaryClicked = useRef(false)
    useEffect(() => {
        if (!props.show) {
            isPrimaryClicked.current = false
        }
    }, [props.show])
    return (
        <ModalBox
            open={props.show}>
            <Box sx={{ p: 3 }}>

                <Stack direction="row" alignItems="center" justifyContent="center">
                    <Typography sx={{ fontSize: { xs: '20px', xl: "22px" }, fontWeight: "500", textAlign: "center", position: "relative", display: "flex", justifyContent: "center", color: theme.palette.mode === "dark" ? theme.palette.primary.contrastText : theme.palette.primary.main }} className={props.variant === "error" ? "error-divider" : "primary-divider"}>{props.title}</Typography>
                </Stack>
                <Box>
                    {props.children}
                </Box>
                {
                    props.consent
                    &&
                    <Grid mt={4} container justifyContent={"center"}>
                        <Grid item xs={12} sm={6}>
                            <TextField label={`Type here "I UNDERSTAND" to continue`} autoComplete={'off'}
                                name='popup'
                                onKeyUp={(e) => {
                                    if (isPrimaryClicked.current) {
                                        return
                                    }
                                    if (e.key === "Enter" && e.target.value === 'I UNDERSTAND') {
                                        isPrimaryClicked.current = true
                                        props.onPrimaryClick({ ...e, detail: 1 }, setIsPrimaryLoading)
                                    }
                                }}
                                variant="outlined" onChange={e => { setDisablePrimary(!(e.target.value === 'I UNDERSTAND')) }} fullWidth />
                        </Grid>
                    </Grid>
                }
                <Box sx={{ mt: 4 }}>
                    <Box sx={{}}>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: "right" }}>
                                    <Button disabled={isSecondaryLoading} color={props.variant} sx={{ textTransform: 'capitalize', fontWeight: 400, ...props.secondaryBtnStyles }} variant="outlined" onClick={(e) => { props.onSecondaryClick(e, setIsSecondaryLoading) }}>
                                        {props.secondaryBtnTxt}
                                        {isSecondaryLoading && <CircularProgress sx={{ width: "22px !important", height: "22px !important", verticalAlign: "middle", ml: 1 }} />}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{}}>
                                    <Button disabled={disablePrimary || isPrimaryLoading} color={props.variant} sx={{ textTransform: 'capitalize', fontWeight: 400, ...props.primaryBtnStyles }} variant="contained" onClick={(e) => props.onPrimaryClick(e, setIsPrimaryLoading)}>
                                        {props.primaryBtnTxt}
                                        {isPrimaryLoading && <CircularProgress sx={{ width: "22px !important", height: "22px !important", verticalAlign: "middle", ml: 1 }} />}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Box>
        </ModalBox >
    )
}
Popup.propTypes = {
    primaryBtnTxt: PropTypes.string,
    secondaryBtnTxt: PropTypes.string,
    show: PropTypes.bool,
    onPrimaryClick: PropTypes.func,
    onSecondaryClick: PropTypes.func,
    variant: PropTypes.string,
    title: PropTypes.string,
    consent: PropTypes.bool,
};
Popup.defaultProps = {
    primaryBtnTxt: 'Ok',
    primaryBtnStyles: {},
    onPrimaryClick: () => { },
    secondaryBtnTxt: 'Cancel',
    secondaryBtnStyles: {},
    onSecondaryClick: () => { },
    show: false,
    title: '',
    variant: 'info',
    consent: false,
};

export default Popup